import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { onColor } from "@/components/portal/PortalDocs";
import { Loader2, PenLine, ShieldCheck } from "lucide-react";

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <label className="block text-sm">
    <span className="text-slate-600 text-xs">{label}</span>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
  </label>
);

// N2: draw-or-type signature pad (canvas); typed falls back to the signer's name in a script face on the PDF.
function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const [mode, setMode] = useState("draw");
  const [has, setHas] = useState(false);
  const pt = (e) => { const r = ref.current.getBoundingClientRect(); const p = e.touches ? e.touches[0] : e; return [p.clientX - r.left, p.clientY - r.top]; };
  const down = (e) => { e.preventDefault(); drawing.current = true; const c = ref.current.getContext("2d"); c.lineWidth = 2; c.lineCap = "round"; c.strokeStyle = "#0b3b86"; const [x, y] = pt(e); c.beginPath(); c.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const c = ref.current.getContext("2d"); const [x, y] = pt(e); c.lineTo(x, y); c.stroke(); setHas(true); };
  const up = () => { if (drawing.current) { drawing.current = false; onChange(ref.current.toDataURL("image/png")); } };
  const clear = () => { const c = ref.current.getContext("2d"); c.clearRect(0, 0, ref.current.width, ref.current.height); setHas(false); onChange(null); };
  return (
    <div>
      <div className="flex items-center gap-3 text-xs text-slate-600 mb-1">
        <span>Signature</span>
        <button type="button" onClick={() => { setMode("draw"); }} className={mode === "draw" ? "font-semibold text-[#003E87]" : ""} data-testid="nda-sig-draw">Draw</button>
        <button type="button" onClick={() => { clear(); setMode("type"); }} className={mode === "type" ? "font-semibold text-[#003E87]" : ""} data-testid="nda-sig-type">Type</button>
        {mode === "draw" && has && <button type="button" onClick={clear} className="text-slate-400 ml-auto" data-testid="nda-sig-clear">Clear</button>}
      </div>
      {mode === "draw" ? (
        <canvas ref={ref} width={520} height={120} className="w-full border border-dashed border-slate-300 rounded-md bg-white touch-none" style={{ height: 120 }}
          onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={up} onTouchStart={down} onTouchMove={move} onTouchEnd={up} data-testid="nda-sig-canvas" />
      ) : <div className="text-xs text-slate-500 border border-dashed border-slate-300 rounded-md p-3" data-testid="nda-sig-typed">Your typed name will be placed as your signature.</div>}
    </div>
  );
}

export function NdaGate({ token, gate, primary, company, onDone }) {
  const c = gate.contact || {};
  const [f, setF] = useState({ name: c.name || "", title: c.title || "", company: gate.company || "", email: c.email || "", address: "" });
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`/public/portal/${token}/nda/sign`, { ...f, signature_image: sig });
      if (r.data.viewer_id) document.cookie = `pr_viewer=${r.data.viewer_id}; path=/; max-age=31536000; SameSite=Lax`;
      onDone(r.data.viewer_id);
    } catch (e) { setErr(e.response?.data?.detail?.message || e.response?.data?.detail || "Could not sign. Please try again."); }
    setBusy(false);
  };
  return (
    <div className="space-y-4" data-testid="nda-gate">
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-6 h-6 shrink-0" style={{ color: primary }} />
        <div>
          <h2 className="text-base font-semibold text-slate-900">{gate.renewal ? "Please renew the mutual NDA to continue" : "Please sign the mutual NDA to continue"}</h2>
          <p className="text-sm text-slate-600 mt-1">{company} shares pricing and project documents under a Mutual Confidentiality and Non-Disclosure Agreement. Read it below, confirm your details, sign once for your company, and your documents open right after. Under a minute.</p>
        </div>
      </div>
      <iframe title="Mutual NDA" src={`${api.defaults.baseURL}/public/nda-blank.pdf#toolbar=0`} className="w-full border border-slate-200 rounded-md" style={{ height: "48vh" }} data-testid="nda-text" />
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Your name" value={f.name} onChange={set("name")} testid="nda-name" />
        <Field label="Title" value={f.title} onChange={set("title")} testid="nda-title" />
        <Field label="Company (the legal name that signs)" value={f.company} onChange={set("company")} testid="nda-company" />
        <Field label="Work email" value={f.email} onChange={set("email")} type="email" testid="nda-email" />
        <div className="sm:col-span-2"><Field label="Company address for notices" value={f.address} onChange={set("address")} testid="nda-address" /></div>
      </div>
      <SignaturePad onChange={setSig} />
      {err && <div className="text-sm text-rose-700" data-testid="nda-error">{String(err)}</div>}
      <button onClick={submit} disabled={busy || !f.name || !f.company || !f.email} className="inline-flex items-center gap-2 text-sm font-semibold rounded-md px-4 py-2.5 disabled:opacity-50" style={{ background: primary, color: onColor(primary) }} data-testid="nda-sign-button">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />} Sign the NDA and open my documents
      </button>
      <p className="text-[11px] text-slate-400">Date, time, IP address, device and a signature ID are recorded. {company} countersigns at the same moment and a copy is emailed to you.</p>
    </div>
  );
}

export function ViewerGate({ token, gate, primary, company, onDone }) {
  const [f, setF] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const submit = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.post(`/public/portal/${token}/viewer`, f);
      if (r.data.ok) { document.cookie = `pr_viewer=${r.data.viewer_id}; path=/; max-age=31536000`; onDone(r.data.viewer_id); }
      else setMsg(r.data.message);
    } catch (e) { setMsg(e.response?.data?.detail || "Please try again."); }
    setBusy(false);
  };
  return (
    <div className="space-y-4" data-testid="viewer-gate">
      <h2 className="text-base font-semibold text-slate-900">Who is reading?</h2>
      <p className="text-sm text-slate-600">These documents are shared with {gate.contact?.name || "your company"} under a signed NDA. Enter your name and work email to continue.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Your name" value={f.name} onChange={(v) => setF((s) => ({ ...s, name: v }))} testid="viewer-name" />
        <Field label="Work email" value={f.email} onChange={(v) => setF((s) => ({ ...s, email: v }))} type="email" testid="viewer-email" />
      </div>
      {msg && <div className="text-sm text-amber-700" data-testid="viewer-message">{msg}</div>}
      <button onClick={submit} disabled={busy || !f.name || !f.email} className="inline-flex items-center gap-2 text-sm font-semibold rounded-md px-4 py-2.5 disabled:opacity-50" style={{ background: primary, color: onColor(primary) }} data-testid="viewer-continue">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Continue
      </button>
      <p className="text-[11px] text-slate-400">{gate.contact?.name ? `${gate.contact.name}: enter your own name and work email too — it takes a second and keeps the record straight.` : ""}</p>
    </div>
  );
}

export function useViewerParam() {
  const [v, setV] = useState(() => new URLSearchParams(window.location.search).get("viewer") || (document.cookie.match(/(?:^|; )pr_viewer=([^;]+)/) || [])[1] || null);
  useEffect(() => {}, []);
  return [v, setV];
}
